import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sprout, CheckCircle, Heart, Users, ArrowRight, Sun, Droplets, Wind, Leaf, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

export default function ClimateActionPage() {
  return (
    <div data-testid="climate-action-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-[#7BC4E2]/30 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-white/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Leaf className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
              Climate Action & Environment
            </h1>
            <p className="text-xl text-white/90">
              Strengthening community resilience and promoting sustainable development
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Images */}
      <section className="relative py-12 overflow-hidden bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_fa85df46-347d-469f-8cb9-d6bc46d2cb89/artifacts/253emsat_WhatsApp%20Image%202026-02-19%20at%2019.46.27.jpeg"
                alt="OPAD Climate Action - Sustainable Agriculture"
                className="w-full h-80 object-cover rounded-3xl shadow-2xl"
              />
              <p className="text-center text-slate-600 mt-4 italic">Women farmers practicing sustainable agriculture</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_fa85df46-347d-469f-8cb9-d6bc46d2cb89/artifacts/9654neiu_WhatsApp%20Image%202026-02-19%20at%2019.46.28.jpeg"
                alt="OPAD Climate Action - Clean Water Access"
                className="w-full h-80 object-cover rounded-3xl shadow-2xl"
              />
              <p className="text-center text-slate-600 mt-4 italic">Community water access programme</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[450px] bg-[#4A7C59]/10 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#7BC4E2]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/90 backdrop-blur-sm p-8 md:p-12 rounded-3xl shadow-lg border border-slate-100">
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                At the <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong>, our Climate Action programmes are designed to strengthen community resilience, reduce environmental vulnerability, and promote sustainable development in regions most affected by climate change.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Recognizing that climate-related challenges such as <strong className="text-[#E08B7A]">extreme weather events, land degradation, and resource scarcity</strong> disproportionately impact vulnerable populations, OPAD works collaboratively with local stakeholders to implement adaptive strategies that enhance environmental sustainability and protect livelihoods.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Through community-based interventions, OPAD supports <strong className="text-[#7BC4E2]">climate-smart agriculture, sustainable land use practices, disaster risk reduction initiatives</strong>, and environmental awareness campaigns that empower communities to respond effectively to climate-related risks. Our programmes focus on integrating climate adaptation and mitigation strategies into local development plans, ensuring long-term resilience and sustainable economic growth.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                We also promote renewable energy solutions, efficient natural resource management, and ecosystem restoration initiatives that safeguard biodiversity and strengthen environmental protection.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg">
                By combining innovation, local knowledge, and strategic partnerships, OPAD advances inclusive climate solutions that not only reduce environmental risks but also improve community well-being and livelihood opportunities. Our work directly contributes to global sustainable development priorities by supporting climate resilience, environmental stewardship, and sustainable resource utilization for present and future generations.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#4A7C59]/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#7BC4E2]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">What We Do</h2>
            <div className="w-24 h-1 bg-[#4A7C59] mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#4A7C59]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 bg-[#4A7C59]/10 rounded-xl flex items-center justify-center mb-4">
                <Wind className="w-7 h-7 text-[#4A7C59]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Climate Adaptation & Community Resilience</h3>
              <p className="text-slate-600">Strengthening communities through climate adaptation and disaster risk reduction initiatives.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#7BC4E2]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 bg-[#7BC4E2]/10 rounded-xl flex items-center justify-center mb-4">
                <Droplets className="w-7 h-7 text-[#7BC4E2]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Sustainable Resource Management</h3>
              <p className="text-slate-600">Promoting responsible use of land, water, and natural resources.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#E5B75F]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-14 h-14 bg-[#E5B75F]/10 rounded-xl flex items-center justify-center mb-4">
                <Sun className="w-7 h-7 text-[#E5B75F]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Renewable Energy & Environmental Awareness</h3>
              <p className="text-slate-600">Supporting clean energy adoption and climate education initiatives.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OPAD Focus Areas */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#E5B75F]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#4A7C59]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
                OPAD Focus Areas
              </h2>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Climate Change Adaptation & Mitigation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E5B75F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Renewable Energy Solutions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Sustainable Natural Resource Management</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Environmental Conservation & Biodiversity Protection</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Community Climate Awareness & Education</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1574943320219-553eb213f72d?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Climate Action and Environment"
                className="rounded-3xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[350px] h-[300px] bg-white/10 rounded-bl-[150px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#7BC4E2]/20 rounded-tr-[120px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Globe className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-6">
              Need Help? Get Involved Today
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Support our climate action initiatives and help protect our planet for future generations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#7BC4E2] hover:bg-[#6AB3D1] text-slate-900 rounded-full px-8 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/get-involved">
                <Button className="bg-white hover:bg-slate-100 text-[#4A7C59] rounded-full px-8 py-6 text-lg font-semibold">
                  <Users className="w-5 h-5 mr-2" />
                  Volunteer With Us
                </Button>
              </Link>
              <Link to="/programs">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#4A7C59] rounded-full px-8 py-6 text-lg font-semibold">
                  View All Programs
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
